import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PreviewData } from '../interfaces/api.interface';
import { SupplierData } from '../interfaces/supplier-data.interface';
import { TagReplacement } from '../interfaces/ai-content.interface';
import { WebsiteGenerationService } from './website-generation.service';
import { WebsiteFileService } from './website-file.service';
import { IndexedDBService } from './indexeddb.service';

/**
 * Service to manage blur states for template elements
 * Provides persistent, state-based blur management
 */
class BlurStateService {
  private blurStates: Map<string, boolean> = new Map();
  private indexedDBService: IndexedDBService;

  constructor(indexedDBService: IndexedDBService) {
    this.indexedDBService = indexedDBService;
  }

  /**
   * Mark an element as permanently unblurred (disabled)
   */
  markAsUnblurred(tagName: string, reason: 'ai' | 'user' | 'manual' | 'fallback'): void {
    // Blur functionality disabled
    console.log(`Blur functionality disabled - markAsUnblurred called for ${tagName} but ignored`);
  }

  /**
   * Mark an element as blurred (placeholder state) (disabled)
   */
  markAsBlurred(tagName: string): void {
    // Blur functionality disabled
    console.log(`Blur functionality disabled - markAsBlurred called for ${tagName} but ignored`);
  }

  /**
   * Check if an element should be blurred (disabled)
   */
  shouldBeBlurred(tagName: string): boolean {
    // Blur functionality disabled - always return false (no blur)
    return false;
  }

  /**
   * Get all blur states as an object (disabled)
   */
  getBlurStates(): { [tagName: string]: boolean } {
    // Blur functionality disabled - return empty object
    return {};
  }

  /**
   * Get all blur states as an object with metadata (disabled)
   */
  getBlurStatesWithMetadata(): { [tagName: string]: { shouldBeBlurred: boolean; reason?: string } } {
    // Blur functionality disabled - return empty object
    return {};
  }

  /**
   * Clear all blur states (disabled)
   */
  clearAllStates(): void {
    // Blur functionality disabled
    console.log('Blur functionality disabled - clearAllStates called but ignored');
  }

  /**
   * Persist blur states to IndexedDB (disabled)
   */
  private async persistToIndexedDB(): Promise<void> {
    // Blur functionality disabled - no persistence
    console.log('Blur functionality disabled - persistToIndexedDB called but ignored');
  }

  /**
   * Restore blur states from IndexedDB (disabled)
   */
  async restoreFromIndexedDB(): Promise<void> {
    // Blur functionality disabled - no restoration
    console.log('Blur functionality disabled - restoreFromIndexedDB called but ignored');
  }

  /**
   * Initialize blur states for all tagged elements in HTML (disabled)
   */
  initializeFromHTML(html: string): void {
    // Blur functionality disabled
    console.log('Blur functionality disabled - initializeFromHTML called but ignored');
  }
}

@Injectable({
  providedIn: 'root'
})
export class PreviewService {
  private previewUpdateSubject = new BehaviorSubject<PreviewData | null>(null);
  public previewUpdate$: Observable<PreviewData | null> = this.previewUpdateSubject.asObservable();

  // Edit mode state
  private isEditModeSubject = new BehaviorSubject<boolean>(false);
  public isEditMode$: Observable<boolean> = this.isEditModeSubject.asObservable();

  private currentTemplateHtml: string = '';
  private originalTemplateHtml: string = '';
  private selectedTemplate: number = 1;
  private selectedTheme: string = 'default';
  private customThemeColor: string = '#2e58d6';
  
  // Store applied TagReplacements for semantic content extraction
  private appliedTagReplacements: TagReplacement[] = [];

  // Blur state management
  private blurStateService: BlurStateService;

  // Global flag to disable blur functionality
  private blurEnabled: boolean = false;

  constructor(
    private websiteGenerationService: WebsiteGenerationService,
    private websiteFileService: WebsiteFileService,
    private indexedDBService: IndexedDBService
  ) {
    this.blurStateService = new BlurStateService(indexedDBService);
    
    // Blur functionality disabled - no event listeners
  }

  /**
   * Check if AI content has been generated (making the site editable)
   */
  hasAIContentBeenGenerated(): boolean {
    return this.appliedTagReplacements.length > 0;
  }

  /**
   * Toggle edit mode on/off
   */
  async toggleEditMode(companyGuid?: string): Promise<void> {
    const currentMode = this.isEditModeSubject.value;
    const newMode = !currentMode;
    this.isEditModeSubject.next(newMode);
    console.log(`🔄 Edit mode ${newMode ? 'enabled' : 'disabled'}`);
    
    // Persist edit mode state
    if (companyGuid) {
      try {
        await this.indexedDBService.storeEditMode(companyGuid, newMode);
        console.log('✅ Edit mode state persisted to IndexedDB');
      } catch (error) {
        console.warn('Failed to persist edit mode state:', error);
      }
    }
    
    // Broadcast blur state update when switching modes
    this.broadcastBlurStateUpdate();
  }

  /**
   * Set edit mode explicitly
   */
  async setEditMode(enabled: boolean, companyGuid?: string): Promise<void> {
    this.isEditModeSubject.next(enabled);
    console.log(`🔄 Edit mode ${enabled ? 'enabled' : 'disabled'}`);
    
    // Persist edit mode state
    if (companyGuid) {
      try {
        await this.indexedDBService.storeEditMode(companyGuid, enabled);
        console.log('✅ Edit mode state persisted to IndexedDB');
      } catch (error) {
        console.warn('Failed to persist edit mode state:', error);
      }
    }
    
    // Broadcast blur state update when switching modes
    this.broadcastBlurStateUpdate();
  }

  /**
   * Get current edit mode state
   */
  getEditMode(): boolean {
    return this.isEditModeSubject.value;
  }

  /**
   * Get access to the WebsiteFileService for blob URL retrieval
   */
  getWebsiteFileService(): WebsiteFileService {
    return this.websiteFileService;
  }

  async initializePreview(templateNumber: number): Promise<void> {
    console.log(`PreviewService: Initializing preview for template ${templateNumber}`);
    
    // Clear previous template data
    this.currentTemplateHtml = '';
    this.originalTemplateHtml = '';
    this.appliedTagReplacements = [];
    
    // Clear blur states for new template
    this.clearBlurStates();
    
    this.selectedTemplate = templateNumber;
    
    // Force reload when switching templates to ensure clean state
    const forceReload = true;
    await this.websiteFileService.initialize(templateNumber, forceReload);
    
    const indexFile = this.websiteFileService.getFileContent('index.html');
    this.currentTemplateHtml = indexFile ? indexFile.content as string : '';
    
    console.log(`PreviewService: Template ${templateNumber} initialized successfully`);
  }

  async updatePreview(supplierData: SupplierData, template?: number, theme?: string, companyGuid?: string): Promise<void> {
    if (template && template !== this.selectedTemplate) {
      this.selectedTemplate = template;
      await this.initializePreview(template);
      await this.updatePreview(supplierData, undefined, theme, companyGuid);
      return;
    }
    
    if (theme) {
      this.selectedTheme = theme;
    }

    await this.websiteFileService.processFiles(supplierData, this.selectedTheme, this.appliedTagReplacements);
    
    const indexFile = this.websiteFileService.getFileContent('index.html');
    this.currentTemplateHtml = indexFile ? indexFile.content as string : '';

    // Initialize blur states from current template HTML
    this.initializeBlurStates();

    // Mark company data as user-provided if it exists in supplier data
    if (supplierData.companyName || supplierData.streetAddress || supplierData.phoneNumber || supplierData.contactEmail) {
      this.markCompanyDataAsUserProvided(supplierData);
    }

    // Mark about content as user-provided if it exists in supplier data
    if (supplierData.aboutContent) {
      this.markAboutContentAsUserProvided(supplierData);
    }

    // Mark capabilities content as user-provided if it exists in supplier data
    if (supplierData.capabilities) {
      this.markCapabilitiesContentAsUserProvided(supplierData);
    }

    // Mark equipment content as user-provided if it exists in supplier data
    if (supplierData.equipment) {
      this.markEquipmentContentAsUserProvided(supplierData);
    }

    // Mark additional services content as user-provided (these are typically AI-generated)
    this.markAdditionalServicesContentAsUserProvided(supplierData);

    const previewData: PreviewData = {
      templateHtml: this.currentTemplateHtml,
      supplierData,
      template: this.selectedTemplate,
      theme: this.selectedTheme,
      customThemeColor: this.selectedTheme === 'custom' ? this.customThemeColor : undefined
    };
    
    console.log('PreviewService: updatePreview - Creating PreviewData:', {
      theme: previewData.theme,
      customThemeColor: previewData.customThemeColor,
      selectedTheme: this.selectedTheme,
      storedCustomColor: this.customThemeColor
    });

    this.previewUpdateSubject.next(previewData);

    // Apply fallback blur removal if AI content exists
    if (this.appliedTagReplacements.length > 0) {
      await this.removeAllBlurEffects();
    }
  }

  async loadTemplatePage(pageName: string, supplierData: SupplierData, companyGuid?: string): Promise<void> {
    try {
      const pageFile = this.websiteFileService.getFileContent(`${pageName}.html`);
      
      if (pageFile) {
        // Initialize blur states from the page-specific HTML to ensure all tags are properly initialized
        this.blurStateService.initializeFromHTML(pageFile.content as string);
        
        const previewData: PreviewData = {
          templateHtml: pageFile.content as string,
          supplierData,
          template: this.selectedTemplate,
          theme: this.selectedTheme,
          customThemeColor: this.selectedTheme === 'custom' ? this.customThemeColor : undefined
        };

        this.previewUpdateSubject.next(previewData);
        
        // Broadcast blur state update when loading a new page
        if (this.appliedTagReplacements.length > 0) {
          this.broadcastBlurStateUpdate();
        }
      } else {
        this.updatePreview(supplierData, undefined, undefined, companyGuid);
      }
    } catch (error) {
      this.updatePreview(supplierData, undefined, undefined, companyGuid);
    }
  }

  generatePreviewHtml(data: PreviewData): string {
    let html = data.templateHtml;

    // Apply custom theme color if theme is 'custom'
    console.log('Preview: generatePreviewHtml - Theme:', data.theme, 'Custom color:', data.customThemeColor);
    if (data.theme === 'custom' && data.customThemeColor) {
      console.log('Preview: Applying custom theme color:', data.customThemeColor);
      const customThemeCSS = `
        <style>
          :root {
            --primary-color: ${data.customThemeColor} !important;
          }
          .btn-primary, .btn, button[type="submit"], input[type="submit"] {
            background-color: ${data.customThemeColor} !important;
            border-color: ${data.customThemeColor} !important;
          }
          .btn-primary:hover, .btn:hover, button[type="submit"]:hover, input[type="submit"]:hover {
            background-color: ${this.darkenColor(data.customThemeColor)} !important;
            border-color: ${this.darkenColor(data.customThemeColor)} !important;
          }
          .text-primary, .text-theme {
            color: ${data.customThemeColor} !important;
          }
          .border-primary, .border-theme {
            border-color: ${data.customThemeColor} !important;
          }
          .bg-primary, .bg-theme {
            background-color: ${data.customThemeColor} !important;
          }
        </style>
      `;
      
      // Insert custom theme CSS in the head
      if (html.includes('</head>')) {
        html = html.replace('</head>', `${customThemeCSS}</head>`);
      } else {
        html = customThemeCSS + html;
      }
    }

    // Add hover detection and edit functionality to tagged elements only if edit mode is enabled
    console.log('Preview: generatePreviewHtml - Edit mode enabled:', this.getEditMode());
    if (this.getEditMode()) {
      console.log('Preview: Adding hover detection to tagged elements');
      html = this.addHoverDetectionToTaggedElements(html);
    } else {
      console.log('Preview: Edit mode disabled, skipping hover detection');
    }

    html = html.replace(/href="css\/([^"]+)"/g, (match, filename) => {
      const cssPath = `css/${filename}`;
      const cssUrl = this.websiteFileService.getBlobUrl(cssPath);
      return cssUrl ? `href="${cssUrl}"` : match;
    });

    html = html.replace(/src="js\/([^"]+)"/g, (match, filename) => {
      const jsPath = `js/${filename}`;
      const jsUrl = this.websiteFileService.getBlobUrl(jsPath);
      return jsUrl ? `src="${jsUrl}"` : match;
    });

    html = html.replace(/src="images\//g, (match, offset, string) => {
      const afterMatch = string.substring(offset + match.length);
      const imageFileMatch = afterMatch.match(/^([^"]*)/);
      if (imageFileMatch) {
        const imagePath = `images/${imageFileMatch[1]}`;
        const imageUrl = this.websiteFileService.getBlobUrl(imagePath);
        return imageUrl ? `src="${imageUrl}"` : match;
      }
      return match;
    });

    html = html.replace(/href="about\.html"/g, `href="javascript:parent.loadTemplatePage('about')"`);
    html = html.replace(/href="capabilities\.html"/g, `href="javascript:parent.loadTemplatePage('capabilities')"`);
    html = html.replace(/href="contact\.html"/g, `href="javascript:parent.loadTemplatePage('contact')"`);
    html = html.replace(/href="quote\.html"/g, `href="javascript:parent.loadTemplatePage('quote')"`);
    html = html.replace(/href="index\.html"/g, `href="javascript:parent.loadTemplatePage('index')"`);

    // Add JavaScript for state-based blur management (only if blur is enabled)
    if (this.blurEnabled) {
    const blurRemovalScript = `
      <script>
        (function() {
          // Simple state-based blur management
          async function applyBlurStates() {
            console.log('🎯 Applying blur states...');
            
            try {
              // Get blur states from parent service
              const blurStates = await window.getBlurStates() || {};
              
              document.querySelectorAll('[tag]').forEach(element => {
              const tagName = element.getAttribute('tag');
                const shouldBeBlurred = blurStates[tagName];
                
                if (shouldBeBlurred === false) {
                  // Element should be unblurred
                  element.classList.remove('blur-section');
                  element.classList.add('content-loaded');
                  console.log('✅ Unblurred:', tagName);
                } else {
                  // Element should be blurred (default state)
                  element.classList.add('blur-section');
                  element.classList.remove('content-loaded');
                  console.log('🔒 Blurred:', tagName);
                }
              });
            } catch (error) {
              console.warn('Failed to apply blur states:', error);
            }
          }
          
          // Initialize blur system immediately
          async function initializeBlurSystem() {
            console.log('🚀 Initializing blur system...');
            await applyBlurStates();
          }
          
          // Run immediately
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initializeBlurSystem);
                  } else {
            initializeBlurSystem();
          }
          
          // Listen for state updates
          window.addEventListener('message', function(event) {
            if (event.data && event.data.type === 'blurStateUpdated') {
              console.log('🔄 Blur state updated, reapplying...');
              applyBlurStates();
            }
          });
          
          // Expose function for manual blur removal
          window.removeBlurFromTag = function(tagName) {
            window.parent.updateBlurState(tagName, false, 'manual');
          };
          
          // Expose function to remove all blur effects via DOM
          window.removeAllBlurEffects = function() {
            console.log('Removing blur effects from all elements via DOM...');
            
            // Find all elements with blur-section class
            var blurredElements = document.querySelectorAll('.blur-section');
            
            for (var i = 0; i < blurredElements.length; i++) {
              var element = blurredElements[i];
              // Remove blur-section class and add content-loaded class
              element.classList.remove('blur-section');
              element.classList.add('content-loaded');
              
              // Get tag name for logging
              var tagName = element.getAttribute('tag') || 'unknown';
              console.log('Unblurred element: ' + tagName);
            }
            
            console.log('Removed blur effects from ' + blurredElements.length + ' elements');
          };
          
          // Expose function for force removal (triggers both DOM and state update)
          window.forceRemoveAllBlurEffects = function() {
            console.log('Force removing all blur effects...');
            window.removeAllBlurEffects();
            // Also notify parent to update state
            window.parent.postMessage({
              type: 'forceRemoveAllBlurEffects'
            }, '*');
          };
        })();
      </script>
    `;
    
    // Insert the script before the closing body tag
    html = html.replace('</body>', `${blurRemovalScript}\n</body>`);

    // Add script to expose blur state methods to iframe
    const exposeBlurStateMethods = `
      <script>
        // Expose blur state methods to iframe
        window.getBlurStates = function() {
          // Get blur states from parent via postMessage
          return new Promise((resolve) => {
            const messageId = Date.now().toString();
            
            // Listen for the response
            const listener = (event) => {
              if (event.data && event.data.type === 'getBlurStatesResponse' && event.data.messageId === messageId) {
                window.removeEventListener('message', listener);
                resolve(event.data.blurStates);
              }
            };
            
            window.addEventListener('message', listener);
            
            // Request blur states from parent
            window.parent.postMessage({
              type: 'getBlurStates',
              messageId: messageId
            }, '*');
            });
          };
          
        window.updateBlurState = function(tagName, shouldBeBlurred, reason) {
          // Send message to parent to update blur state
          window.parent.postMessage({
            type: 'updateBlurState',
            tagName: tagName,
            shouldBeBlurred: shouldBeBlurred,
            reason: reason
          }, '*');
        };
      </script>
    `;
    
    // Insert the exposure script in the head
    if (html.includes('</head>')) {
      html = html.replace('</head>', `${exposeBlurStateMethods}</head>`);
    } else {
      html = exposeBlurStateMethods + html;
      }
    }

    return html;
  }

  setTemplate(templateNumber: number): void {
    this.selectedTemplate = templateNumber;
  }

  setTheme(theme: string): void {
    this.selectedTheme = theme;
  }

  setCustomThemeColor(color: string): void {
    // Store custom theme color for use in preview
    console.log('PreviewService: Setting custom theme color:', color);
    this.customThemeColor = color;
    
    // If theme is currently 'custom', trigger immediate preview update
    if (this.selectedTheme === 'custom') {
      const currentPreview = this.previewUpdateSubject.value;
      if (currentPreview) {
        const updatedPreview: PreviewData = {
          ...currentPreview,
          customThemeColor: color
        };
        this.previewUpdateSubject.next(updatedPreview);
      }
    }
  }

  getCustomThemeColor(): string {
    return this.customThemeColor;
  }

  private darkenColor(color: string): string {
    // Simple color darkening function
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Darken by 20%
    const factor = 0.8;
    const newR = Math.round(r * factor);
    const newG = Math.round(g * factor);
    const newB = Math.round(b * factor);
    
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  }

  getCurrentTemplate(): number {
    return this.selectedTemplate;
  }

  getCurrentTheme(): string {
    return this.selectedTheme;
  }

  async getDeploymentZip(): Promise<File> {
    const blob = await this.websiteFileService.packageForDeployment();
    return new File([blob], 'website.zip', { type: 'application/zip' });
  }

  getFileCount(): number {
    return this.websiteFileService.getFileCount();
  }

  /**
   * Remove blur effects from all placeholder elements for deployment
   * This method modifies the HTML to remove blur effects before deployment
   */
  removeBlurEffectsForDeployment(): void {
    console.log('🎯 PreviewService: Removing blur effects for deployment');
    
    // Update all HTML files to remove blur effects
    const htmlFiles = ['index.html', 'about.html', 'capabilities.html', 'contact.html', 'quote.html'];
    
    htmlFiles.forEach(fileName => {
      const fileContent = this.websiteFileService.getFileContent(fileName);
      if (fileContent && typeof fileContent.content === 'string') {
        let html = fileContent.content as string;
        
        // Remove blur-section class from all elements
        html = html.replace(/class="([^"]*)\s*blur-section\s*([^"]*)"/g, (match, before, after) => {
          const classes = `${before} ${after}`.trim().replace(/\s+/g, ' ');
          return classes ? `class="${classes}"` : '';
        });
        
        // Also remove standalone blur-section classes
        html = html.replace(/class="blur-section"/g, '');
        
        // Update the file content
        this.websiteFileService.updateFileContent(fileName, html);
        console.log(`✅ PreviewService: Removed blur effects from ${fileName}`);
      }
    });
    
    // Update the current template HTML
    if (this.currentTemplateHtml) {
      this.currentTemplateHtml = this.currentTemplateHtml.replace(/class="([^"]*)\s*blur-section\s*([^"]*)"/g, (match, before, after) => {
        const classes = `${before} ${after}`.trim().replace(/\s+/g, ' ');
        return classes ? `class="${classes}"` : '';
      });
      this.currentTemplateHtml = this.currentTemplateHtml.replace(/class="blur-section"/g, '');
    }
  }

  async getTemplateHtml(): Promise<string> {
    return this.currentTemplateHtml;
  }

  async getOriginalTagContent(tagName: string): Promise<string> {
    const htmlToSearch = this.originalTemplateHtml || this.currentTemplateHtml;
    
    const tagPattern = new RegExp(`<([^>]*tag="${tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*)>([^<]*)</[^>]*>`, 'i');
    const match = htmlToSearch.match(tagPattern);
    
    if (match) {
      return match[2] || '';
    }

    const selfClosingPattern = new RegExp(`<[^>]*tag="${tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>`, 'i');
    const selfClosingMatch = htmlToSearch.match(selfClosingPattern);
    
    if (selfClosingMatch) {
      return 'Original content';
    }

    return 'Original content';
  }

  async applyAIContent(tagReplacements: TagReplacement[], companyGuid?: string): Promise<void> {
    try {
      if (!this.originalTemplateHtml) {
        this.originalTemplateHtml = this.currentTemplateHtml;
      }

      // Store the applied tag replacements for semantic content extraction
      this.appliedTagReplacements = [...tagReplacements];

      // Enable edit mode when AI content is first applied
      if (!this.getEditMode()) {
        this.setEditMode(true);
      }

      let updatedHtml = this.currentTemplateHtml;

      // Define company tags that should preserve user-provided content
      const companyTags = ['COMPANY_NAME', 'COMPANY_ADDRESS', 'COMPANY_PHONE', 'COMPANY_EMAIL'];

      for (const replacement of tagReplacements) {
        // Skip company tags to preserve user-provided content
        if (companyTags.includes(replacement.tagName)) {
          console.log(`🔄 Skipping AI content application for company tag: ${replacement.tagName}`);
          continue;
        }
        
        const tagPattern = new RegExp(
          `(<[^>]*tag="${replacement.tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>)(.*?)(</[^>]*>)`,
          'gi'
        );

        updatedHtml = updatedHtml.replace(tagPattern, (match, openTag, content, closeTag) => {
          return `${openTag}${replacement.replacementHTML}${closeTag}`;
        });
      }

      this.currentTemplateHtml = updatedHtml;

      const htmlFiles = ['index.html', 'about.html', 'capabilities.html', 'contact.html', 'quote.html'];
      
      for (const fileName of htmlFiles) {
        const fileContent = this.websiteFileService.getFileContent(fileName);
        if (fileContent && typeof fileContent.content === 'string') {
          // CRITICAL FIX: Apply AI content replacements to the CURRENT (placeholder-replaced) content
          // instead of starting from original template content
          let fileHtml = fileContent.content;
          
          for (const replacement of tagReplacements) {
            // Skip company tags to preserve user-provided content
            if (companyTags.includes(replacement.tagName)) {
              console.log(`🔄 Skipping AI content application for company tag in ${fileName}: ${replacement.tagName}`);
              continue;
            }
            
            const tagPattern = new RegExp(
              `(<[^>]*tag="${replacement.tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>)(.*?)(</[^>]*>)`,
              'gi'
            );

            fileHtml = fileHtml.replace(tagPattern, (match, openTag, content, closeTag) => {
              return `${openTag}${replacement.replacementHTML}${closeTag}`;
            });
          }
          
          // Update file content while preserving placeholder replacements
          this.websiteFileService.updateFileContent(fileName, fileHtml);
        }
      }

      const currentPreview = this.previewUpdateSubject.value;
      if (currentPreview) {
        const updatedPreview: PreviewData = {
          ...currentPreview,
          templateHtml: updatedHtml
        };
        this.previewUpdateSubject.next(updatedPreview);
      }

      // PERSIST AI CONTENT TO INDEXEDDB
      if (companyGuid) {
        try {
          await this.persistAIContentToIndexedDB(companyGuid, tagReplacements);
        } catch (error) {
          console.warn('Failed to persist AI content to IndexedDB:', error);
        }
      }

      // Mark only NEW AI-generated content as unblurred (preserve existing user content)
      for (const replacement of tagReplacements) {
        // Skip company tags that already have user-provided content
        if (companyTags.includes(replacement.tagName)) {
          console.log(`🔄 Skipping company tag ${replacement.tagName} - preserving user content`);
          continue;
        }
        
        // Mark as unblurred only if it's not already marked as user-provided
        if (!this.blurStateService.shouldBeBlurred(replacement.tagName)) {
          console.log(`🔄 Tag ${replacement.tagName} already unblurred, skipping`);
          continue;
        }
        
        this.blurStateService.markAsUnblurred(replacement.tagName, 'ai');
        console.log(`✅ Marked AI content as unblurred: ${replacement.tagName}`);
      }

      // Apply fallback blur removal after AI content
      await this.removeAllBlurEffects();

      console.log(`✅ Applied AI content with fallback blur removal`);

    } catch (error) {
      throw error;
    }
  }

  /**
   * Add hover detection and edit functionality to tagged elements in HTML
   */
  private addHoverDetectionToTaggedElements(html: string): string {
    // Add CSS for hover effects (no edit icon)
    const hoverCSS = `
      <style>
        .editable-element {
          position: relative;
          transition: box-shadow 0.2s cubic-bezier(0.4,0,0.2,1);
          cursor: pointer;
        }
        
        .editable-element:hover,
        .editable-element.editing-active {
          /* Remove outline and border, add heavy boxy shadow */
          outline: none;
          border: none;
          border-radius: 6px;
          box-shadow: 0 8px 32px 0 rgba(46, 88, 214, 0.32), 0 2px 12px 0 rgba(0,0,0,0.18), 0 0 0 4px rgba(46, 88, 214, 0.10);
        }
        
        .floating-edit-tooltip {
          display: none;
          position: fixed;
          background: #2e58d6;
          color: #fff;
          padding: 6px 14px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          white-space: nowrap;
          box-shadow: 0 2px 8px rgba(46, 88, 214, 0.18);
          z-index: 99999;
          pointer-events: none;
          opacity: 0.97;
          left: 0;
          top: 0;
          transition: opacity 0.15s;
        }
      </style>
    `;

    // Add JavaScript for edit functionality (no edit icon)
    const editScript = `
      <script>
        function initializeEditFunctionality() {
          console.log('Edit functionality: Initializing edit functionality');
          
          // Create a single floating tooltip
          let tooltip = document.getElementById('floating-edit-tooltip');
          if (!tooltip) {
            tooltip = document.createElement('div');
            tooltip.id = 'floating-edit-tooltip';
            tooltip.className = 'floating-edit-tooltip';
            tooltip.innerText = 'Click to edit content';
            document.body.appendChild(tooltip);
          }

          let lastActive = null;
          
          // Function to generate a unique tag name for untagged images
          function generateImageTagName(imgElement) {
            const alt = imgElement.alt || '';
            const src = imgElement.src || '';
            
            // Try to extract meaningful name from alt text
            if (alt && alt.trim()) {
              return 'IMG_' + alt.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
            }
            
            // For blob URLs, extract the blob ID (this is the key fix)
            if (src && src.startsWith('blob:')) {
              const blobId = src.split('/').pop();
              if (blobId) {
                return 'IMG_' + blobId.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
              }
            }
            
            // Try to extract from src filename
            if (src) {
              const filename = src.split('/').pop().split('.')[0];
              if (filename) {
                return 'IMG_' + filename.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
              }
            }
            
            // Fallback to consistent hash-based naming (same logic as TypeScript)
            function hashString(str) {
              let hash = 0;
              for (let i = 0; i < str.length; i++) {
                const char = str.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash; // Convert to 32-bit integer
              }
              // Convert to positive integer and then to base36 string
              return Math.abs(hash).toString(36).substr(0, 8);
            }
            
            const srcHash = hashString(src || 'default');
            return 'IMG_' + srcHash.toUpperCase();
          }
          
          // Get all tagged elements
          const taggedElements = document.querySelectorAll('[tag]');
          
          // Get all img elements that don't have tags
          const untaggedImages = document.querySelectorAll('img:not([tag])');
          
          // Combine both sets of elements
          const editableElements = [...taggedElements, ...untaggedImages];
          
          console.log('Edit functionality: Found', editableElements.length, 'editable elements');
          console.log('Edit functionality: Tagged elements:', taggedElements.length);
          console.log('Edit functionality: Untagged images:', untaggedImages.length);
          
          // Log details about untagged images
          untaggedImages.forEach((img, index) => {
            console.log('Edit functionality: Untagged image', index, ':', img.src, img.alt);
          });
          
          editableElements.forEach(element => {
            element.classList.add('editable-element');
            
            // Generate tag for untagged images
            if (element.tagName.toLowerCase() === 'img' && !element.hasAttribute('tag')) {
              const generatedTag = generateImageTagName(element);
              element.setAttribute('tag', generatedTag);
            }

            element.addEventListener('mouseenter', function(e) {
              const rect = element.getBoundingClientRect();
              tooltip.style.display = 'block';
              tooltip.style.left = (rect.left + rect.width/2 - tooltip.offsetWidth/2) + 'px';
              tooltip.style.top = (rect.top - tooltip.offsetHeight - 8) + 'px';
              tooltip.style.opacity = '0.97';
              
              // Update tooltip text based on element type
              if (element.tagName.toLowerCase() === 'img') {
                tooltip.innerText = 'Click to change image';
              } else {
                tooltip.innerText = 'Click to edit content';
              }
            });
            element.addEventListener('mousemove', function(e) {
              const rect = element.getBoundingClientRect();
              tooltip.style.left = (rect.left + rect.width/2 - tooltip.offsetWidth/2) + 'px';
              tooltip.style.top = (rect.top - tooltip.offsetHeight - 8) + 'px';
            });
            element.addEventListener('mouseleave', function(e) {
              tooltip.style.display = 'none';
            });
            element.addEventListener('click', function(e) {
              e.preventDefault();
              e.stopPropagation();
              console.log('Edit functionality: Element clicked:', element.tagName, element.getAttribute('tag'));
              
              // Remove .editing-active from any previously active element
              if (lastActive) lastActive.classList.remove('editing-active');
              element.classList.add('editing-active');
              lastActive = element;
              const tagName = element.getAttribute('tag');
              const rect = element.getBoundingClientRect();
              
              // Check if this is an image element
              if (element.tagName.toLowerCase() === 'img') {
                const currentImageSrc = element.src || '';
                console.log('Edit functionality: Sending image upload message for tag:', tagName, 'src:', currentImageSrc);
                // Always use postMessage for iframe communication
                window.parent.postMessage({
                  type: 'openImageUploader',
                  tagName: tagName,
                  currentImageSrc: currentImageSrc,
                  elementRect: {
                    left: rect.left,
                    top: rect.top,
                    width: rect.width,
                    height: rect.height
                  }
                }, '*');
              } else {
                // Handle text content editing
                const textContent = element.textContent || '';
                const innerText = element.innerText || '';
                const currentContent = textContent || innerText || '';
                console.log('Edit functionality: Sending content editor message for tag:', tagName, 'content:', currentContent);
                // Always use postMessage for iframe communication
                window.parent.postMessage({
                  type: 'openContentEditor',
                  tagName: tagName,
                  currentContent: currentContent,
                  elementRect: {
                    left: rect.left,
                    top: rect.top,
                    width: rect.width,
                    height: rect.height
                  }
                }, '*');
              }
            });
          });

          // Listen for a message from parent to clear editing highlight
          window.addEventListener('message', function(event) {
            if (event.data && event.data.type === 'closeInlineEditor') {
              if (lastActive) lastActive.classList.remove('editing-active');
              lastActive = null;
            }
          });
        }
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', initializeEditFunctionality);
        } else {
          initializeEditFunctionality();
        }
        window.addEventListener('load', function() {
          setTimeout(initializeEditFunctionality, 100);
        });
      </script>
    `;

    // Insert CSS and JavaScript into the HTML
    let modifiedHtml = html;
    
    // Add CSS to head
    if (modifiedHtml.includes('</head>')) {
      modifiedHtml = modifiedHtml.replace('</head>', `${hoverCSS}</head>`);
    } else {
      modifiedHtml = hoverCSS + modifiedHtml;
    }
    
    // Add JavaScript before closing body tag
    if (modifiedHtml.includes('</body>')) {
      modifiedHtml = modifiedHtml.replace('</body>', `${editScript}</body>`);
    } else {
      modifiedHtml = modifiedHtml + editScript;
    }
    
    return modifiedHtml;
  }

  /**
   * Remove blur from a specific tag element (legacy method for backward compatibility)
   */
  removeBlurFromTag(tagName: string): void {
    console.log('🎯 Legacy removeBlurFromTag called for tag:', tagName);
    // Use the new state-based system
    this.updateBlurState(tagName, false, 'manual');
  }

  /**
   * Update content in the current preview for a specific tag
   */
  async updateContentInPreview(tagName: string, newContent: string, companyGuid?: string): Promise<void> {
    try {
      // Update the current template HTML
      const tagPattern = new RegExp(
        `(<[^>]*tag="${tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>)([^<]*)(</[^>]*>)`,
        'gi'
      );

      this.currentTemplateHtml = this.currentTemplateHtml.replace(tagPattern, (match, openTag, content, closeTag) => {
        return `${openTag}${newContent}${closeTag}`;
      });

      // Update the applied tag replacements
      const existingIndex = this.appliedTagReplacements.findIndex(r => r.tagName === tagName);
      if (existingIndex >= 0) {
        this.appliedTagReplacements[existingIndex].replacementHTML = newContent;
      } else {
        this.appliedTagReplacements.push({
          tagName,
          replacementHTML: newContent
        });
      }

      // Update all HTML files
      const htmlFiles = ['index.html', 'about.html', 'capabilities.html', 'contact.html', 'quote.html'];
      
      for (const fileName of htmlFiles) {
        const fileContent = this.websiteFileService.getFileContent(fileName);
        if (fileContent && typeof fileContent.content === 'string') {
          let fileHtml = fileContent.content;
          
          fileHtml = fileHtml.replace(tagPattern, (match, openTag, content, closeTag) => {
            return `${openTag}${newContent}${closeTag}`;
          });
          
          this.websiteFileService.updateFileContent(fileName, fileHtml);
        }
      }

      // Update the preview
      const currentPreview = this.previewUpdateSubject.value;
      if (currentPreview) {
        const updatedPreview: PreviewData = {
          ...currentPreview,
          templateHtml: this.currentTemplateHtml
        };
        this.previewUpdateSubject.next(updatedPreview);
      }

      // Save state after content update
      if (companyGuid) {
        try {
          // CRITICAL FIX: Use merge approach for individual updates to maintain consistency
          // Create a single tag replacement object for the updated content
          const updatedReplacement = {
            tagName: tagName,
            replacementHTML: newContent
          };
          
          // Merge the single update with existing replacements
          await this.indexedDBService.mergeTagReplacements(companyGuid, [updatedReplacement], 'newer');
        } catch (error) {
          console.warn('Failed to save state after content update:', error);
        }
      }

      // Mark the updated content as unblurred
      this.blurStateService.markAsUnblurred(tagName, 'user');
      
      // Broadcast blur state update to iframe
      this.broadcastBlurStateUpdate();

      console.log(`✅ Updated content for tag "${tagName}" in preview`);
    } catch (error) {
      console.error(`❌ Failed to update content for tag "${tagName}":`, error);
      throw error;
    }
  }

  /**
   * Update image in the current preview for a specific tag
   */
  async updateImageInPreview(tagName: string, newImageSrc: string, companyGuid?: string): Promise<void> {
    try {
      const isS3Url = newImageSrc.includes('s3.amazonaws.com') || newImageSrc.includes('dev-supplier-website-images.s3');
      const urlType = isS3Url ? 'S3' : 'Blob';
      console.log(`🔍 updateImageInPreview: Starting for tag "${tagName}" with ${urlType} URL: ${newImageSrc.substring(0, 50)}...`);
      
      // Function to generate tag name for untagged images (same logic as in JavaScript)
      const generateImageTagName = (imgSrc: string, alt: string = '') => {
        if (alt && alt.trim()) {
          return 'IMG_' + alt.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
        }
        
        // For blob URLs, extract the blob ID (this is the key fix)
        if (imgSrc && imgSrc.startsWith('blob:')) {
          const blobId = imgSrc.split('/').pop();
          if (blobId) {
            return 'IMG_' + blobId.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
          }
        }
        
        if (imgSrc) {
          const filename = imgSrc.split('/').pop()?.split('.')[0];
          if (filename) {
            return 'IMG_' + filename.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
          }
        }
        
        // Use consistent hash-based fallback
        const srcHash = this.hashString(imgSrc || 'default');
        return 'IMG_' + srcHash.toUpperCase();
      };

      // Update the current template HTML - handle both tagged and untagged images
      let updatedHtml = this.currentTemplateHtml;
      let replacementsMade = 0;
      
      // First, try to find images with explicit tags
      const taggedImgPattern = new RegExp(
        `(<img[^>]*tag="${tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*src=")[^"]*("[^>]*)>`,
        'gi'
      );
      
      // Check if there are any tagged images before replacing
      const taggedMatches = updatedHtml.match(taggedImgPattern);
      console.log(`🔍 updateImageInPreview: Found ${taggedMatches?.length || 0} tagged images for "${tagName}"`);
      
      if (taggedMatches && taggedMatches.length > 0) {
        // Replace tagged images
        updatedHtml = updatedHtml.replace(taggedImgPattern, (match, beforeSrc, afterSrc) => {
          console.log(`🔍 updateImageInPreview: Replacing tagged image:`, match);
          replacementsMade++;
          return `${beforeSrc}${newImageSrc}${afterSrc}>`;
        });
      }
      
      // If no tagged images were found/replaced, look for untagged images
      if (replacementsMade === 0) {
        console.log(`🔍 updateImageInPreview: No tagged images found, checking untagged images for "${tagName}"`);
        
        // Find all img tags and check if any would generate the same tag name
        const imgPattern = /<img[^>]*src="([^"]*)"[^>]*>/gi;
        updatedHtml = updatedHtml.replace(imgPattern, (match, imgSrc) => {
          // Extract alt attribute if present
          const altMatch = match.match(/alt="([^"]*)"/);
          const alt = altMatch ? altMatch[1] : '';
          
          // Generate tag name for this image
          const generatedTag = generateImageTagName(imgSrc, alt);
          
          // If this image would generate the same tag name, update it
          if (generatedTag === tagName) {
            console.log(`🔍 updateImageInPreview: Found matching untagged image, updating:`, match);
            replacementsMade++;
            // Add the tag attribute and update the src
            return match.replace(/src="[^"]*"/, `src="${newImageSrc}"`).replace(/<img/, `<img tag="${tagName}"`);
          }
          
          return match;
        });
      }

      console.log(`🔍 updateImageInPreview: Total replacements made: ${replacementsMade}`);

      this.currentTemplateHtml = updatedHtml;

      // Update the applied tag replacements
      const existingIndex = this.appliedTagReplacements.findIndex(r => r.tagName === tagName);
      if (existingIndex >= 0) {
        this.appliedTagReplacements[existingIndex].replacementHTML = newImageSrc;
      } else {
        this.appliedTagReplacements.push({
          tagName,
          replacementHTML: newImageSrc
        });
      }

      // Update all HTML files
      const htmlFiles = ['index.html', 'about.html', 'capabilities.html', 'contact.html', 'quote.html'];
      
      for (const fileName of htmlFiles) {
        const fileContent = this.websiteFileService.getFileContent(fileName);
        if (fileContent && typeof fileContent.content === 'string') {
          let fileHtml = fileContent.content;
          let fileReplacementsMade = 0;
          
          // Apply the same logic to file HTML
          const taggedImgPattern = new RegExp(
            `(<img[^>]*tag="${tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*src=")[^"]*("[^>]*)>`,
            'gi'
          );
          
          const fileTaggedMatches = fileHtml.match(taggedImgPattern);
          if (fileTaggedMatches && fileTaggedMatches.length > 0) {
            fileHtml = fileHtml.replace(taggedImgPattern, (match, beforeSrc, afterSrc) => {
              fileReplacementsMade++;
              return `${beforeSrc}${newImageSrc}${afterSrc}>`;
            });
          }
          
          // Handle untagged images in file if no tagged images were found
          if (fileReplacementsMade === 0) {
            const imgPattern = /<img[^>]*src="([^"]*)"[^>]*>/gi;
            fileHtml = fileHtml.replace(imgPattern, (match, imgSrc) => {
              const altMatch = match.match(/alt="([^"]*)"/);
              const alt = altMatch ? altMatch[1] : '';
              const generatedTag = generateImageTagName(imgSrc, alt);
              
              if (generatedTag === tagName) {
                fileReplacementsMade++;
                return match.replace(/src="[^"]*"/, `src="${newImageSrc}"`).replace(/<img/, `<img tag="${tagName}"`);
              }
              
              return match;
            });
          }
          
          if (fileReplacementsMade > 0) {
            this.websiteFileService.updateFileContent(fileName, fileHtml);
            console.log(`🔍 updateImageInPreview: Updated file ${fileName} with ${fileReplacementsMade} replacements`);
          }
        }
      }

      // Update the preview
      const currentPreview = this.previewUpdateSubject.value;
      if (currentPreview) {
        const updatedPreview: PreviewData = {
          ...currentPreview,
          templateHtml: ('' + this.currentTemplateHtml) // force new string reference
        };
        setTimeout(() => this.previewUpdateSubject.next(updatedPreview), 0);
      }

      // Save state after image update
      if (companyGuid) {
        try {
          // Update stored tag replacements
          const existingReplacements = await this.indexedDBService.getTagReplacements(companyGuid) || [];
          const updatedReplacements = existingReplacements.map(r => 
            r.tagName === tagName ? { ...r, replacementHTML: newImageSrc } : r
          );
          await this.indexedDBService.storeTagReplacements(companyGuid, updatedReplacements);
        } catch (error) {
          console.warn('Failed to save state after image update:', error);
        }
      }

      // Mark the updated image as unblurred
      this.blurStateService.markAsUnblurred(tagName, 'user');
      
      // Broadcast blur state update to iframe
      this.broadcastBlurStateUpdate();

      console.log(`✅ updateImageInPreview: Successfully updated image for tag "${tagName}" with ${replacementsMade} replacements using ${urlType} URL`);
    } catch (error) {
      console.error(`❌ updateImageInPreview: Failed to update image for tag "${tagName}":`, error);
      throw error;
    }
  }

  /**
   * Replace an image blob in the file service and update indexDB
   */
  async replaceImageBlob(tagName: string, imageBlob: Blob, companyGuid?: string): Promise<string | null> {
    try {
      // Find the image file path based on the tag name
      const imagePath = this.findImagePathByTag(tagName);
      
      if (imagePath) {
        // Update the image file in the website file service with the new blob
        this.websiteFileService.updateFileWithBlob(imagePath, imageBlob);
        
        // Regenerate blob URLs to ensure the updated content is properly mapped
        this.websiteFileService.regenerateBlobUrls();
        
        // Sync the updated files to IndexedDB (using existing mechanism)
        await this.websiteFileService.syncToIndexedDB();
        
        // Get the blob URL for the updated content
        const blobUrl = this.websiteFileService.getBlobUrl(imagePath);
        
        // Trigger a preview refresh so the updated content is displayed
        const currentPreview = this.previewUpdateSubject.value;
        if (currentPreview) {
          this.previewUpdateSubject.next({
            ...currentPreview,
            templateHtml: this.currentTemplateHtml
          });
        }
        
        console.log(`✅ Replaced image blob for tag "${tagName}" at path "${imagePath}"`);
        console.log(`📍 Image path maintained: ${imagePath}`);
        console.log(`🔗 Blob URL: ${blobUrl}`);
        console.log(`💾 Changes synced to IndexedDB`);
        
        return blobUrl;
      } else {
        console.warn(`❌ Could not find image path for tag "${tagName}"`);
        return null;
      }
    } catch (error) {
      console.error(`❌ Failed to replace image blob for tag "${tagName}":`, error);
      throw error;
    }
  }

  /**
   * Generate a consistent hash from a string for tag naming
   */
  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    // Convert to positive integer and then to base36 string
    return Math.abs(hash).toString(36).substr(0, 8);
  }

  /**
   * Find the image file path based on the tag name
   */
  private findImagePathByTag(tagName: string): string | null {
    // Look for the image in the current template HTML
    const htmlToSearch = this.currentTemplateHtml || this.originalTemplateHtml;
    
    // First, try to find images with explicit tags
    const taggedImgPattern = new RegExp(
      `<img[^>]*tag="${tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*src="([^"]*)"[^>]*>`,
      'gi'
    );
    
    const taggedMatch = taggedImgPattern.exec(htmlToSearch);
    if (taggedMatch) {
      return taggedMatch[1];
    }
    
    // If no tagged images found, look for untagged images that might match the generated tag
    const imgPattern = /<img[^>]*src="([^"]*)"[^>]*>/gi;
    let imgMatch;
    
    while ((imgMatch = imgPattern.exec(htmlToSearch)) !== null) {
      const fullImgTag = imgMatch[0];
      const imgSrc = imgMatch[1];
      
      // Extract alt attribute if present
      const altMatch = fullImgTag.match(/alt="([^"]*)"/);
      const alt = altMatch ? altMatch[1] : '';
      
      // Generate tag name for this image (same logic as in JavaScript)
      const generateImageTagName = (imgSrc: string, alt: string = '') => {
        if (alt && alt.trim()) {
          return 'IMG_' + alt.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
        }
        
        // For blob URLs, extract the blob ID (this is the key fix)
        if (imgSrc && imgSrc.startsWith('blob:')) {
          const blobId = imgSrc.split('/').pop();
          if (blobId) {
            return 'IMG_' + blobId.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
          }
        }
        
        if (imgSrc) {
          const filename = imgSrc.split('/').pop()?.split('.')[0];
          if (filename) {
            return 'IMG_' + filename.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
          }
        }
        
        // Use a consistent fallback based on image src hash for consistent tag generation
        const srcHash = this.hashString(imgSrc || 'default');
        return 'IMG_' + srcHash.toUpperCase();
      };
      
      const generatedTag = generateImageTagName(imgSrc, alt);
      
      // If this image would generate the same tag name, return its path
      if (generatedTag === tagName) {
        return imgSrc;
      }
    }
    
    return null;
  }

  /**
   * Extract current semantic content from stored TagReplacements for three-context change requests
   */
  getCurrentSemanticContent(): { [tagName: string]: string } {
    const semanticContent: { [tagName: string]: string } = {};
    
    // Convert stored TagReplacements to semantic content object
    for (const replacement of this.appliedTagReplacements) {
      semanticContent[replacement.tagName] = replacement.replacementHTML;
    }
    
    return semanticContent;
  }

  /**
   * Get stored TagReplacements for backward compatibility
   */
  getStoredTagReplacements(): TagReplacement[] {
    return [...this.appliedTagReplacements];
  }

  /**
   * Extract all semantic tags from current template HTML (including those without stored replacements)
   */
  extractAllSemanticTags(): { [tagName: string]: string } {
    const semanticContent: { [tagName: string]: string } = {};
    const htmlToSearch = this.currentTemplateHtml || this.originalTemplateHtml;
    
    // Extract all semantic tags from HTML (text content)
    const tagPattern = /<[^>]*tag="([^"]+)"[^>]*>([^<]*)<\/[^>]*>/gi;
    let match;
    
    while ((match = tagPattern.exec(htmlToSearch)) !== null) {
      const tagName = match[1];
      const currentContent = match[2];
      
      // Check if we have a stored replacement for this tag
      const storedReplacement = this.appliedTagReplacements.find(r => r.tagName === tagName);
      
      if (storedReplacement) {
        // Use the stored AI-generated content
        semanticContent[tagName] = storedReplacement.replacementHTML;
      } else {
        // Use the current content from HTML
        semanticContent[tagName] = currentContent.trim();
      }
    }
    
    // Extract all img tags (both tagged and untagged)
    const imgPattern = /<img[^>]*src="([^"]*)"[^>]*>/gi;
    let imgMatch;
    
    while ((imgMatch = imgPattern.exec(htmlToSearch)) !== null) {
      const fullImgTag = imgMatch[0];
      const imgSrc = imgMatch[1];
      
      // Check if this img has a tag attribute
      const tagMatch = fullImgTag.match(/tag="([^"]+)"/);
      let tagName: string;
      
      if (tagMatch) {
        // Use existing tag
        tagName = tagMatch[1];
      } else {
        // Generate a tag name for untagged images
        const altMatch = fullImgTag.match(/alt="([^"]*)"/);
        const alt = altMatch ? altMatch[1] : '';
        const filename = imgSrc.split('/').pop()?.split('.')[0] || '';
        
        if (alt && alt.trim()) {
          tagName = 'IMG_' + alt.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
        } else if (imgSrc && imgSrc.startsWith('blob:')) {
          // For blob URLs, extract the blob ID (this is the key fix)
          const blobId = imgSrc.split('/').pop();
          if (blobId) {
            tagName = 'IMG_' + blobId.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
          } else {
            const srcHash = this.hashString(imgSrc || 'default');
            tagName = 'IMG_' + srcHash.toUpperCase();
          }
        } else if (filename) {
          tagName = 'IMG_' + filename.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
        } else {
          // Use consistent hash-based fallback
          const srcHash = this.hashString(imgSrc || 'default');
          tagName = 'IMG_' + srcHash.toUpperCase();
        }
      }
      
      // Check if we have a stored replacement for this tag
      const storedReplacement = this.appliedTagReplacements.find(r => r.tagName === tagName);
      
      if (storedReplacement) {
        // Use the stored AI-generated content
        semanticContent[tagName] = storedReplacement.replacementHTML;
      } else {
        // Use the current image src
        semanticContent[tagName] = imgSrc;
      }
    }
    
    return semanticContent;
  }

  /**
   * Apply updated semantic content from change request response
   */
  async applyUpdatedSemanticContent(updatedSemanticContent: { [tagName: string]: string }): Promise<void> {
    try {
      // Convert updated semantic content back to TagReplacement format
      const updatedTagReplacements: TagReplacement[] = [];
      
      for (const [tagName, content] of Object.entries(updatedSemanticContent)) {
        updatedTagReplacements.push({
          tagName,
          replacementHTML: content
        });
      }
      
      // Apply the updated content
      await this.applyAIContent(updatedTagReplacements);
      
    } catch (error) {
      console.error('❌ Failed to apply updated semantic content:', error);
      throw error;
    }
  }

  /**
   * Clear stored TagReplacements (useful for reset operations)
   */
  clearStoredTagReplacements(): void {
    this.appliedTagReplacements = [];
    console.log('🔄 Cleared stored tag replacements');
  }

  async resetToOriginalContent(): Promise<void> {
    if (this.originalTemplateHtml) {
      this.currentTemplateHtml = this.originalTemplateHtml;
      
      // Clear stored tag replacements when resetting
      this.clearStoredTagReplacements();
      
      // Clear all blur states and reinitialize from original HTML
      this.clearBlurStates();
      this.initializeBlurStates();
      
      const htmlFiles = ['index.html', 'about.html', 'capabilities.html', 'contact.html', 'quote.html'];
      
      for (const fileName of htmlFiles) {
        const fileContent = this.websiteFileService.getFileContent(fileName);
        if (fileContent && typeof fileContent.content === 'string') {
          if (fileName === 'index.html') {
            this.websiteFileService.updateFileContent(fileName, this.originalTemplateHtml);
          }
        }
      }
      
      const currentPreview = this.previewUpdateSubject.value;
      if (currentPreview) {
        const updatedPreview: PreviewData = {
          ...currentPreview,
          templateHtml: this.originalTemplateHtml
        };
        this.previewUpdateSubject.next(updatedPreview);
      }
    }
  }



  /**
   * Persist AI content to IndexedDB for state restoration
   */
  private async persistAIContentToIndexedDB(companyGuid: string, tagReplacements: TagReplacement[]): Promise<void> {
    try {
      // CRITICAL FIX: Use merge instead of overwrite to preserve all generated tags
      // This ensures that equipment tags 1-6 and other tags are not lost when new content is generated
      await this.indexedDBService.mergeTagReplacements(companyGuid, tagReplacements, 'newer');
      
      // Store AI content state
      const aiContentState = {
        tagReplacements,
        hasChanges: true,
        appliedAt: new Date().toISOString()
      };
      await this.indexedDBService.storeAIContent(companyGuid, aiContentState);
      
      console.log('✅ AI content persisted to IndexedDB for state restoration (merged)');
    } catch (error) {
      console.error('❌ Failed to persist AI content to IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Restore AI content from IndexedDB on initialization
   */
  async restoreAIContentFromIndexedDB(companyGuid: string): Promise<void> {
    try {
      const storedTagReplacements = await this.indexedDBService.getTagReplacements(companyGuid);
      const storedAIContent = await this.indexedDBService.getAIContent(companyGuid);
      
      if (storedTagReplacements && storedTagReplacements.length > 0) {
        console.log('🔄 Restoring AI content from IndexedDB...');
        
        // Restore the applied tag replacements
        this.appliedTagReplacements = storedTagReplacements;
        
        // Apply the AI content to the current template
        await this.applyAIContent(storedTagReplacements);
        
        // Mark only NEW AI-generated content as unblurred (preserve existing user content)
        const companyTags = ['COMPANY_NAME', 'COMPANY_ADDRESS', 'COMPANY_PHONE', 'COMPANY_EMAIL'];
        
        for (const replacement of storedTagReplacements) {
          // Skip company tags that already have user-provided content
          if (companyTags.includes(replacement.tagName)) {
            console.log(`🔄 Skipping company tag ${replacement.tagName} during restore - preserving user content`);
            continue;
          }
          
          // Mark as unblurred only if it's not already marked as user-provided
          if (!this.blurStateService.shouldBeBlurred(replacement.tagName)) {
            console.log(`🔄 Tag ${replacement.tagName} already unblurred during restore, skipping`);
            continue;
          }
          
          this.blurStateService.markAsUnblurred(replacement.tagName, 'ai');
          console.log(`✅ Marked AI content as unblurred during restore: ${replacement.tagName}`);
        }
        
        // Broadcast blur state update to iframe
        this.broadcastBlurStateUpdate();
        
        console.log('✅ AI content restored from IndexedDB');
      } else {
        console.log('ℹ️ No stored AI content found in IndexedDB');
      }
    } catch (error) {
      console.error('❌ Failed to restore AI content from IndexedDB:', error);
    }
  }

  /**
   * Trigger blur removal in the preview iframe (legacy method for backward compatibility)
   */
  public triggerBlurRemoval(): void {
    console.log('🔄 Legacy triggerBlurRemoval called');
    // Use the new state-based system
    this.broadcastBlurStateUpdate();
  }

  /**
   * Restore edit mode state from IndexedDB on initialization
   */
  async restoreEditModeFromIndexedDB(companyGuid: string): Promise<void> {
    try {
      const storedEditMode = await this.indexedDBService.getEditMode(companyGuid);
      
      if (storedEditMode !== null) {
        console.log('🔄 Restoring edit mode state from IndexedDB:', storedEditMode);
        this.isEditModeSubject.next(storedEditMode);
        console.log('✅ Edit mode state restored from IndexedDB');
      } else {
        console.log('ℹ️ No stored edit mode state found in IndexedDB');
      }
    } catch (error) {
      console.error('❌ Failed to restore edit mode state from IndexedDB:', error);
    }
  }

  /**
   * Get blur states for iframe communication (disabled)
   * This method is exposed to the iframe via postMessage
   */
  getBlurStates(): { [tagName: string]: boolean } {
    // Blur functionality disabled - return empty object
    return {};
  }

  /**
   * Update blur state for a specific tag (disabled)
   * This method is exposed to the iframe via postMessage
   */
  updateBlurState(tagName: string, shouldBeBlurred: boolean, reason: 'ai' | 'user' | 'manual' | 'fallback'): void {
    // Blur functionality disabled
    console.log('Blur functionality disabled - updateBlurState called but ignored');
  }

  /**
   * Broadcast blur state update to iframe (disabled)
   */
  private broadcastBlurStateUpdate(): void {
    // Blur functionality disabled
    console.log('Blur functionality disabled - broadcastBlurStateUpdate called but ignored');
  }

  /**
   * Initialize blur states from current template HTML (disabled)
   */
  private initializeBlurStates(): void {
    // Blur functionality disabled
    console.log('Blur functionality disabled - initializeBlurStates called but ignored');
  }

  /**
   * Restore blur states from IndexedDB (disabled)
   */
  async restoreBlurStatesFromIndexedDB(companyGuid: string): Promise<void> {
    // Blur functionality disabled
    console.log('Blur functionality disabled - restoreBlurStatesFromIndexedDB called but ignored');
  }

  /**
   * Clear all blur states (disabled)
   */
  clearBlurStates(): void {
    // Blur functionality disabled
    console.log('Blur functionality disabled - clearBlurStates called but ignored');
  }

  /**
   * Simple fallback: Remove blur effect from all elements with blur-section class (disabled)
   * This ensures no content remains blurred after AI generation
   */
  async removeAllBlurEffects(): Promise<void> {
    // Blur functionality disabled
    console.log('Blur functionality disabled - removeAllBlurEffects called but ignored');
  }

  /**
   * Manual trigger to remove all blur effects (disabled)
   * Useful for debugging or user-initiated actions
   */
  async forceRemoveAllBlurEffects(): Promise<void> {
    // Blur functionality disabled
    console.log('Blur functionality disabled - forceRemoveAllBlurEffects called but ignored');
  }

  /**
   * Debug method to check current blur states (disabled)
   */
  debugBlurStates(): void {
    // Blur functionality disabled
    console.log('Blur functionality disabled - debugBlurStates called but ignored');
  }

  /**
   * Mark company data as user-provided content (disabled)
   */
  markCompanyDataAsUserProvided(supplierData?: SupplierData): void {
    // Blur functionality disabled
    console.log('Blur functionality disabled - markCompanyDataAsUserProvided called but ignored');
  }

  /**
   * Mark about content as user-provided when about content is entered in chat (disabled)
   */
  markAboutContentAsUserProvided(supplierData?: SupplierData): void {
    // Blur functionality disabled
    console.log('Blur functionality disabled - markAboutContentAsUserProvided called but ignored');
  }

  /**
   * Mark capabilities content as user-provided when capabilities are entered in chat (disabled)
   */
  markCapabilitiesContentAsUserProvided(supplierData?: SupplierData): void {
    // Blur functionality disabled
    console.log('Blur functionality disabled - markCapabilitiesContentAsUserProvided called but ignored');
  }

  /**
   * Mark equipment content as user-provided when equipment is entered in chat (disabled)
   */
  markEquipmentContentAsUserProvided(supplierData?: SupplierData): void {
    // Blur functionality disabled
    console.log('Blur functionality disabled - markEquipmentContentAsUserProvided called but ignored');
  }

  /**
   * Mark additional services content as user-provided when services are entered in chat (disabled)
   */
  markAdditionalServicesContentAsUserProvided(supplierData?: SupplierData): void {
    // Blur functionality disabled
    console.log('Blur functionality disabled - markAdditionalServicesContentAsUserProvided called but ignored');
  }

  /**
   * Check if company data has been provided by user
   */
  hasCompanyDataBeenProvided(): boolean {
    const companyTags = ['COMPANY_NAME', 'COMPANY_ADDRESS', 'COMPANY_PHONE', 'COMPANY_EMAIL'];
    
    for (const tagName of companyTags) {
      if (this.blurStateService.shouldBeBlurred(tagName)) {
        return false; // At least one company tag is still blurred
      }
    }
    
    return true; // All company tags are unblurred
  }

  /**
   * Check if about content has been provided by user
   */
  hasAboutContentBeenProvided(): boolean {
    const aboutTags = ['ABOUT_SECTION_TITLE', 'ABOUT_MAIN_HEADING', 'ABOUT_DESCRIPTION_1', 'ABOUT_DESCRIPTION_2', 'COMPANY_ABOUT'];
    
    for (const tagName of aboutTags) {
      if (this.blurStateService.shouldBeBlurred(tagName)) {
        return false; // At least one about tag is still blurred
      }
    }
    
    return true; // All about tags are unblurred
  }

  /**
   * Check if capabilities content has been provided by user
   */
  hasCapabilitiesContentBeenProvided(): boolean {
    const capabilitiesTags = ['CAPABILITIES_SECTION_TITLE', 'CAPABILITIES_MAIN_HEADING', 'CAPABILITIES_DESCRIPTION', 'CAPABILITIES_DESCRIPTION_2'];
    
    for (const tagName of capabilitiesTags) {
      if (this.blurStateService.shouldBeBlurred(tagName)) {
        return false; // At least one capabilities tag is still blurred
      }
    }
    
    return true; // All capabilities tags are unblurred
  }

  /**
   * Check if equipment content has been provided by user
   */
  hasEquipmentContentBeenProvided(): boolean {
    const equipmentTags = [
      'EQUIPMENT_SECTION_TITLE', 'EQUIPMENT_INTRO_DESCRIPTION',
      'EQUIPMENT_1_TITLE', 'EQUIPMENT_1_DESCRIPTION',
      'EQUIPMENT_2_TITLE', 'EQUIPMENT_2_DESCRIPTION',
      'EQUIPMENT_3_TITLE', 'EQUIPMENT_3_DESCRIPTION',
      'EQUIPMENT_4_TITLE', 'EQUIPMENT_4_DESCRIPTION',
      'EQUIPMENT_5_TITLE', 'EQUIPMENT_5_DESCRIPTION',
      'EQUIPMENT_6_TITLE', 'EQUIPMENT_6_DESCRIPTION'
    ];
    
    for (const tagName of equipmentTags) {
      if (this.blurStateService.shouldBeBlurred(tagName)) {
        return false; // At least one equipment tag is still blurred
      }
    }
    
    return true; // All equipment tags are unblurred
  }

  /**
   * Check if additional services content has been provided by user
   */
  hasAdditionalServicesContentBeenProvided(): boolean {
    const additionalServicesTags = [
      'ADDITIONAL_SERVICES_TITLE',
      'ADDITIONAL_SERVICE_1_TITLE', 'ADDITIONAL_SERVICE_1_DESCRIPTION',
      'ADDITIONAL_SERVICE_2_TITLE', 'ADDITIONAL_SERVICE_2_DESCRIPTION'
    ];
    
    for (const tagName of additionalServicesTags) {
      if (this.blurStateService.shouldBeBlurred(tagName)) {
        return false; // At least one additional services tag is still blurred
      }
    }
    
    return true; // All additional services tags are unblurred
  }

  /**
   * Debug method to log current blur states for about tags
   */
  debugAboutBlurStates(): void {
    const aboutTags = ['ABOUT_SECTION_TITLE', 'ABOUT_MAIN_HEADING', 'ABOUT_DESCRIPTION_1', 'ABOUT_DESCRIPTION_2', 'COMPANY_ABOUT'];
    const blurStates = this.blurStateService.getBlurStates();
    
    console.log('🔍 Debug: About tag blur states:');
    aboutTags.forEach(tagName => {
      const shouldBeBlurred = blurStates[tagName];
      console.log(`  ${tagName}: ${shouldBeBlurred === false ? '✅ Unblurred' : '🔒 Blurred'}`);
    });
  }

  /**
   * Debug method to log current blur states for capabilities tags
   */
  debugCapabilitiesBlurStates(): void {
    const capabilitiesTags = ['CAPABILITIES_SECTION_TITLE', 'CAPABILITIES_MAIN_HEADING', 'CAPABILITIES_DESCRIPTION', 'CAPABILITIES_DESCRIPTION_2'];
    const blurStates = this.blurStateService.getBlurStates();
    
    console.log('🔍 Debug: Capabilities tag blur states:');
    capabilitiesTags.forEach(tagName => {
      const shouldBeBlurred = blurStates[tagName];
      console.log(`  ${tagName}: ${shouldBeBlurred === false ? '✅ Unblurred' : '🔒 Blurred'}`);
    });
  }

  /**
   * Debug method to log current blur states for equipment tags
   */
  debugEquipmentBlurStates(): void {
    const equipmentTags = [
      'EQUIPMENT_SECTION_TITLE', 'EQUIPMENT_INTRO_DESCRIPTION',
      'EQUIPMENT_1_TITLE', 'EQUIPMENT_1_DESCRIPTION',
      'EQUIPMENT_2_TITLE', 'EQUIPMENT_2_DESCRIPTION',
      'EQUIPMENT_3_TITLE', 'EQUIPMENT_3_DESCRIPTION',
      'EQUIPMENT_4_TITLE', 'EQUIPMENT_4_DESCRIPTION',
      'EQUIPMENT_5_TITLE', 'EQUIPMENT_5_DESCRIPTION',
      'EQUIPMENT_6_TITLE', 'EQUIPMENT_6_DESCRIPTION'
    ];
    const blurStates = this.blurStateService.getBlurStates();
    
    console.log('🔍 Debug: Equipment tag blur states:');
    equipmentTags.forEach(tagName => {
      const shouldBeBlurred = blurStates[tagName];
      console.log(`  ${tagName}: ${shouldBeBlurred === false ? '✅ Unblurred' : '🔒 Blurred'}`);
    });
  }

  /**
   * Debug method to log current blur states for additional services tags
   */
  debugAdditionalServicesBlurStates(): void {
    const additionalServicesTags = [
      'ADDITIONAL_SERVICES_TITLE',
      'ADDITIONAL_SERVICE_1_TITLE', 'ADDITIONAL_SERVICE_1_DESCRIPTION',
      'ADDITIONAL_SERVICE_2_TITLE', 'ADDITIONAL_SERVICE_2_DESCRIPTION'
    ];
    const blurStates = this.blurStateService.getBlurStates();
    
    console.log('🔍 Debug: Additional Services tag blur states:');
    additionalServicesTags.forEach(tagName => {
      const shouldBeBlurred = blurStates[tagName];
      console.log(`  ${tagName}: ${shouldBeBlurred === false ? '✅ Unblurred' : '🔒 Blurred'}`);
    });
  }

} 